import React, { useState, useMemo } from 'react';
import { Check, Minus, Plus, FileDown } from 'lucide-react';
import { formatINR } from './utils';
import { Chapter } from './StoryHero';

export const DecorCalculator = ({ decor, venueName }) => {
  const [quantities, setQuantities] = useState({});
  const addons = decor?.addons || [];
  const basePrice = decor?.base_package?.price || 0;

  const selectedAddons = useMemo(
    () => addons.map((a, i) => ({ ...a, qty: quantities[i] || 0, index: i })).filter(a => a.qty > 0),
    [addons, quantities]
  );
  const addonsTotal = selectedAddons.reduce((sum, a) => sum + a.qty * a.price, 0);
  const total = basePrice + addonsTotal;

  const changeQty = (i, delta) => {
    setQuantities(prev => ({ ...prev, [i]: Math.max(0, (prev[i] || 0) + delta) }));
  };

  const downloadQuote = () => {
    const rows = selectedAddons.map(a => `<tr><td>${a.name} (× ${a.qty})</td><td style="text-align:right">${formatINR(a.qty * a.price)}</td></tr>`).join('');
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Decor Quote - ${venueName}</title>
      <style>body{font-family:Georgia,serif;color:#0A1710;padding:40px;max-width:640px;margin:0 auto}
      h1{font-size:22px;border-bottom:2px solid #C6A87C;padding-bottom:12px}
      table{width:100%;border-collapse:collapse;margin-top:24px}td{padding:10px 0;border-bottom:1px solid #E5DCCF;font-size:14px}
      .total td{font-weight:bold;font-size:18px;border-top:2px solid #1A3626;border-bottom:none}
      .note{font-size:11px;color:#748278;margin-top:24px;line-height:1.6}</style></head><body>
      <h1>${venueName} — Décor Estimate</h1>
      <p style="font-size:12px;color:#748278">Generated on ${new Date().toLocaleDateString('en-IN')} · Wedding Venues Company</p>
      <table>
        <tr><td>${decor?.base_package?.name || 'Base decor package'}</td><td style="text-align:right">${formatINR(basePrice)}</td></tr>
        ${rows}
        <tr class="total"><td>Approximate total</td><td style="text-align:right">${formatINR(total)}</td></tr>
      </table>
      <p class="note">${decor?.disclaimer || ''}</p>
      </body></html>`;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(html);
    win.document.close();
    setTimeout(() => win.print(), 400);
  };

  if (!decor?.base_package) return null;

  return (
    <section id="calculator" className="bg-[#F3EFEB] py-16 md:py-24 font-wvc-body" data-testid="story-decor">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-10">
        <div className="max-w-2xl">
          <Chapter n={6} label={decor.kicker || 'Décor Packages & Add Ons'} />
          <h2 className="font-wvc-display text-4xl sm:text-5xl font-light text-[#0A1710] tracking-tight leading-[1.05] mt-6">{decor.title || decor.question}</h2>
          {decor.intro && <p className="text-[#1A3626]/70 mt-5 text-sm font-light leading-relaxed">{decor.intro}</p>}
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="bg-[#FDFCF8] border border-[#E5DCCF] p-6 md:p-8" data-testid="decor-base-package">
              <div className="flex items-baseline justify-between gap-4 border-b-2 border-[#0A1710] pb-4">
                <div>
                  <p className="text-[9px] uppercase tracking-[0.25em] text-[#C6A87C] font-bold">Included · Base Package</p>
                  <h4 className="font-wvc-display text-2xl text-[#0A1710] mt-1">{decor.base_package.name}</h4>
                </div>
                <span className="font-wvc-display text-3xl text-[#0A1710] whitespace-nowrap tabular-nums">{formatINR(basePrice)}</span>
              </div>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                {(decor.base_package.inclusions || []).map((inc, i) => (
                  <p key={i} className="flex items-start gap-2.5 text-[13px] text-[#1A3626]/80 font-light">
                    <Check className="w-3.5 h-3.5 text-[#C6A87C] mt-0.5 flex-shrink-0" strokeWidth={1.5} /> {inc}
                  </p>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <p className="text-[9px] uppercase tracking-[0.25em] text-[#C6A87C] font-bold mb-3">Make It Yours · Add-On Services</p>
              <div className="bg-[#FDFCF8] border border-[#E5DCCF]">
                {addons.map((a, i) => (
                  <div key={i} className={`flex items-center gap-4 px-5 py-3.5 ${i > 0 ? 'border-t border-[#E5DCCF]' : ''}`} data-testid={`decor-addon-${i}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-bold text-[#0A1710]">{a.name}</p>
                      <p className="text-[11px] text-[#1A3626]/50 mt-0.5 font-light">{formatINR(a.price)} {a.unit}{a.note ? ` · ${a.note}` : ''}</p>
                    </div>
                    <div className="flex items-center">
                      <button
                        onClick={() => changeQty(i, -1)}
                        disabled={!quantities[i]}
                        className="w-8 h-8 flex items-center justify-center border border-[#E5DCCF] hover:bg-[#1A3626] hover:text-[#FDFCF8] transition-colors disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-inherit"
                        data-testid={`decor-addon-dec-${i}`}
                        aria-label={`Remove one ${a.name}`}
                      >
                        <Minus className="w-3 h-3" strokeWidth={1.5} />
                      </button>
                      <span className="w-9 text-center text-[13px] font-bold tabular-nums" data-testid={`decor-addon-qty-${i}`}>{quantities[i] || 0}</span>
                      <button
                        onClick={() => changeQty(i, 1)}
                        className="w-8 h-8 flex items-center justify-center border border-[#E5DCCF] hover:bg-[#1A3626] hover:text-[#FDFCF8] transition-colors"
                        data-testid={`decor-addon-inc-${i}`}
                        aria-label={`Add one ${a.name}`}
                      >
                        <Plus className="w-3 h-3" strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 lg:sticky lg:top-20">
            <div className="bg-[#1A3626] text-[#FDFCF8] p-7 md:p-9 border border-[#2A4D38]">
              <p className="text-[9px] uppercase tracking-[0.3em] text-[#C6A87C] font-bold">Your Décor Estimate</p>
              <div className="mt-5 space-y-2.5 text-[13px] tabular-nums border-t border-[#2A4D38] pt-4">
                <div className="flex justify-between gap-4">
                  <span className="text-[#FDFCF8]/60 font-light">{decor.base_package.name}</span>
                  <span>{formatINR(basePrice)}</span>
                </div>
                {selectedAddons.map(a => (
                  <div key={a.index} className="flex justify-between gap-4" data-testid={`decor-summary-addon-${a.index}`}>
                    <span className="text-[#FDFCF8]/60 font-light">{a.name} × {a.qty}</span>
                    <span>{formatINR(a.qty * a.price)}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-[#C6A87C] mt-5 pt-5 flex items-baseline justify-between gap-4">
                <span className="text-[9px] uppercase tracking-[0.25em] text-[#C6A87C] font-bold">Approx. Total</span>
                <span className="font-wvc-display text-4xl font-light tabular-nums" data-testid="decor-total">{formatINR(total)}</span>
              </div>
              <button
                onClick={downloadQuote}
                className="mt-7 w-full inline-flex items-center justify-center gap-2.5 bg-[#C6A87C] text-[#0A1710] py-4 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-[#FDFCF8] transition-colors"
                data-testid="decor-download-pdf"
              >
                <FileDown className="w-4 h-4" strokeWidth={1.5} /> Download Décor Quote (PDF)
              </button>
              {decor.disclaimer && <p className="text-[10px] text-[#FDFCF8]/40 mt-4 leading-relaxed font-light">{decor.disclaimer}</p>}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
